/** Parsed identifiers from a prepaid membership redemption QR / link. */
export interface PrepaidRedemptionScanPayload {
  prepaidPurchaseId?: string;
  redemptionToken?: string;
  confirmationNumber?: string;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const REDEMPTION_PATH_SEGMENTS = ['redeem', 'prepaid', 'prepaid-redeem', 'check-in', 'checkin'];

const PURCHASE_ID_QUERY_KEYS = [
  'prepaid_purchase_id',
  'prepaidPurchaseId',
  'purchase_id',
  'purchaseId',
];

const TOKEN_QUERY_KEYS = ['token', 'redemption_token', 'redemptionToken'];

const CONFIRMATION_QUERY_KEYS = [
  'confirmation',
  'confirmation_number',
  'confirmationNumber',
  'confirmation_no',
];

/** Strip decorative `#` prefixes from confirmation numbers shown in emails. */
export function normalizeConfirmationNumber(raw: string): string {
  return raw.replace(/^#+/g, '').trim();
}

function firstQueryValue(
  params: URLSearchParams,
  keys: readonly string[]
): string | undefined {
  for (const key of keys) {
    const value = params.get(key)?.trim();
    if (value) {
      return value;
    }
  }
  return undefined;
}

function extractPurchaseIdFromPath(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean);
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i].trim();
    if (REDEMPTION_PATH_SEGMENTS.includes(segment.toLowerCase())) {
      const next = segments[i + 1]?.trim();
      if (next && (UUID_PATTERN.test(next) || next.length >= 8)) {
        return next;
      }
    }
    if (UUID_PATTERN.test(segment)) {
      return segment;
    }
  }
  return undefined;
}

function looksLikeRedemptionUrl(url: URL): boolean {
  const path = url.pathname.toLowerCase();
  if (REDEMPTION_PATH_SEGMENTS.some((segment) => path.includes(`/${segment}`))) {
    return true;
  }
  return (
    PURCHASE_ID_QUERY_KEYS.some((key) => url.searchParams.has(key)) ||
    TOKEN_QUERY_KEYS.some((key) => url.searchParams.has(key)) ||
    CONFIRMATION_QUERY_KEYS.some((key) => url.searchParams.has(key))
  );
}

/**
 * Parse a decoded QR string into prepaid redemption identifiers.
 * Returns null when the text is not a recognizable redemption URL.
 */
export function parsePrepaidRedemptionUrl(text: string): PrepaidRedemptionScanPayload | null {
  const trimmed = text?.trim();
  if (!trimmed) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null;
  }

  if (!looksLikeRedemptionUrl(url)) {
    return null;
  }

  const prepaidPurchaseId =
    firstQueryValue(url.searchParams, PURCHASE_ID_QUERY_KEYS) ||
    extractPurchaseIdFromPath(url.pathname);

  const redemptionToken = firstQueryValue(url.searchParams, TOKEN_QUERY_KEYS);

  const confirmationRaw = firstQueryValue(url.searchParams, CONFIRMATION_QUERY_KEYS);
  const confirmationNumber = confirmationRaw
    ? normalizeConfirmationNumber(confirmationRaw)
    : undefined;

  if (!prepaidPurchaseId && !redemptionToken && !confirmationNumber) {
    return null;
  }

  return {
    prepaidPurchaseId: prepaidPurchaseId || undefined,
    redemptionToken: redemptionToken || undefined,
    confirmationNumber: confirmationNumber || undefined,
  };
}
